import { EPSILON } from "../constants";

export function clamp(value: number, min: number, max: number) {
  return Math.max(Math.min(value, max), min);
}

/** Returns the fraction of the way between min and max (or their defaults)
 * that number is, clamped to [0, 1], or undefined if min and max are the same. */
export function normalize(value: number, min = 0, max = 1): number | undefined {
  const valMin = min;
  const valMax = max;
  const valRange = valMax - valMin;

  // If the range is zero (min and max are effectively the same), return undefined
  if (isApproximately(valRange, 0)) {
    return undefined;
  }

  // Normalize value within the range [valMin, valMax] and clamp to [0, 1]
  const normalizedValue = (value - valMin) / valRange;
  return Math.min(Math.max(normalizedValue, 0), 1);
}

export function normalizeAngle(a: number) {
  return ((a % 360) + 360) % 360 > 180 ? (((a % 360) + 360) % 360) - 360 : ((a % 360) + 360) % 360;
}

/** Returns the value in the middle (the median). */
export function mid<Ord>(x0: Ord, x1: Ord, x2: Ord) {
  // Permutations of ordering, min to max, and comparisons.
  //    x1<x2-,
  //  x0<x1-, | ,-x2<x0
  // 0 1 2  | | |
  // 7 8 9  T T F  x1 (a)
  // 7 9 8  T F F  x2   (c)
  // 8 9 7  T F T  x0  (b)
  // 8 7 9  F T F  x0   (e)
  // 9 7 8  F T T  x2  (d)
  // 9 8 7  F F T  x1   (f)
  // prettier-ignore
  return (
    x0 < x1 ?
      x1 < x2 ? x1  // x0 < x1 < x2 (a)
      : x2 < x0 ? x0 // x2 < x0 < x1 (b)
      : x2 // x1 >= x2 >= x0 (c)
    : x1 < x2 ? 
      x2 < x0 ? x2 // x1 < x2 < x0 (d)
      : x0 // x2 >= x0 >= x1 (e)
    : x1 // (f)
  );
}

/** Linearly interpolates between `y0` and `y1` based on the position of `x` in `[x0, x1]`,
 * clamping to the nearest endpoint if out of range. */
export function lerpClamp2(x: number, x0: number, x1: number, y0: number, y1: number) {
  const t = (x - x0) / (x1 - x0);
  return mid(y0, (1 - t) * y0 + t * y1, y1);
}

/**
 * @summary Checks if two numbers are approximately equal.
 * @param a - The first number.
 * @param b - The second number.
 * @param epsilon - The maximum allowed difference between the two numbers.
 */
export function isApproximately(a: number, b: number, epsilon = EPSILON) {
  return a === b || Math.abs(a - b) <= epsilon;
}
